import { smooth_step } from '@inter-ikea-betrakta/viewer';
import { ComponentIds, HierarchyUtils, MathUtils, Transform, Vector } from '@inter-ikea-core/core';
import { CameraBehaviorComponent, Movement, getAngleAdjustedDirection, getDefaultAngleAdjustment } from '..';
import { defaultPadding, assemblySingleImagePadding, focusSolutionDelay, orbitLimits, zoomLimits } from './CameraApi';
import { OrbitPlannerCamera } from '@inter-ikea-betrakta/viewer/dist/modules/cameras/OrbitPlannerCamera';
import { InputGatherer } from '@inter-ikea-betrakta/viewer/dist/modules/InputGatherer';
import { CustomComponentIds } from '@inter-ikea-kvadrat/kvadrat';
export class BaseCameraApi {
    constructor(sceneManager, ipexViewer, entityManager, coreApi, canvas) {
        // In cases where we want to reset the camera
        // This creates a new camera and pushes it to the camera stack
        this.resetCamera = () => {
            const newCamera = this.createCamera();
            const cameraManager = this.ipexViewer.get_camera_manager();
            if (!cameraManager) {
                throw Error('No camera manager found, are you sure the scene is initialized?');
            }
            this.setLimits(newCamera);
            cameraManager.push_camera(newCamera, {
                duration_seconds: 0.1,
                interpolation: smooth_step
            });
        };
        this.loadEmtpyScene = (isMobile) => {
            const position = { x: 0, y: 1, z: isMobile ? 5.3 : 6 };
            const target = { x: 0, y: 1, z: 1 };
            const newCamera = this.createCamera(position, target);
            this.setLimits(newCamera);
            const cameraManager = this.ipexViewer.get_camera_manager();
            if (!cameraManager)
                return;
            cameraManager.push_camera(newCamera, {
                duration_seconds: 2,
                interpolation: smooth_step
            });
        };
        this.getWallFacingDirection = (direction) => {
            const walls = this.coreApi.entities.getByComponent(CustomComponentIds.ROOM_WALL_COMPONENT_ID);
            if (!walls.length)
                throw new Error('No walls found in the scene');
            const { wall } = walls.reduce((acc, curr) => {
                const worldTransform = curr.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID);
                const normal = worldTransform?.transform.forward;
                const diff = direction.dot(normal ?? new Vector(0, 0, 0));
                return diff > acc.diff ? { wall: curr, diff } : acc;
            }, {
                wall: walls[0],
                diff: -1
            });
            return wall;
        };
        this.sceneManager = sceneManager;
        this.ipexViewer = ipexViewer;
        const camera = this.createCamera();
        this.setLimits(camera);
        // Create the input gatherer to collect inputs to the Viewer
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const input_gatherer = new InputGatherer(canvas, this.ipexViewer);
        this.screenshotTool = [];
        this.coreApi = coreApi;
        // Create camera entity
        this.cameraEntity = entityManager.createEntity();
        this.cameraEntity.addComponent(new CameraBehaviorComponent({ entityIds: [] }));
        const cameraManager = this.ipexViewer.get_camera_manager();
        cameraManager?.push_camera(camera);
    }
    getCameraEntity() {
        return this.cameraEntity;
    }
    getCameraTransform() {
        // Get camera position
        const camera = this.getActiveCamera();
        if (!camera)
            throw new Error('No active camera found');
        const { x, y, z } = camera.position;
        const position = new Vector(x, y, z).times(1000);
        const target = this.getCameraTarget();
        const transform = new Transform(position, MathUtils.quaternionFromLookDirection(target.minus(position).normalize(), new Vector(0, 1, 0)));
        return transform;
    }
    centerRoom() {
        // get all room part entities
        const roomParts = this.coreApi.entities?.getByComponent(ComponentIds.ROOM_PART_COMPONENT_ID);
        if (!roomParts)
            return;
        // create a new camera and center on the room center
        const camera = this.createCamera();
        const roomPartsObjectId = roomParts
            .map(rp => {
            return rp
                .getComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)
                ?.viewerObject.getViewerObjectId();
        })
            .filter(Boolean);
        camera.frame_objects(roomPartsObjectId, true, 0, { x: 0, y: 0, z: -1 }).catch(e => {
            this.coreApi.reportErrorToSentry(new Error(e), { roomPartsObjectId });
            // If framing objects fails, we will reset the camera
            this.resetCamera();
        });
        const cameraManager = this.ipexViewer.get_camera_manager();
        if (!cameraManager)
            return;
        cameraManager.set_camera_stack([camera]);
    }
    // Gets world direction according to local movement of camera.
    getCameraDirection(movement) {
        const transform = this.getCameraTransform();
        switch (movement) {
            case Movement.LEFT:
                return transform.right;
            case Movement.RIGHT:
                return transform.right.times(-1);
            case Movement.UP:
                return transform.up;
            case Movement.DOWN:
                return transform.up.times(-1);
        }
    }
    getCameraTarget() {
        const camera = this.getActiveCamera();
        if (!camera)
            throw new Error('No active camera found');
        const { x, y, z } = camera.forward;
        const target = new Vector(x, y, z).times(1000);
        return target;
    }
    // This is the base settings for the camera that we use for all ranges
    setLimits(camera, isMobile) {
        if (isMobile) {
            camera.set_orbit_limits(orbitLimits.FREE);
            camera.set_zoom_limits(zoomLimits.MOBILE);
        }
        else {
            camera.set_orbit_limits(orbitLimits.FREE);
            camera.set_zoom_limits(zoomLimits.DEFAULT);
        }
    }
    async focusSolution(direction, margin, isMobile, shouldFocus) {
        const frames = this.coreApi.entities?.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
        // When loading an empty scene, we will create a new camera and push it to the camera stack with a zoomed in view
        if (frames.length === 0) {
            this.loadEmtpyScene(isMobile);
            return;
        }
        const frame = frames[0];
        const frameRotationTransform = frame?.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform.rotation;
        if (!frameRotationTransform) {
            // eslint-disable-next-line no-console
            console.log('No frame rotation found');
        }
        if (!direction) {
            const onlyAdjustAngleVertically = this.coreApi.entities.entityIsPartOfAngledSolution(frames);
            const defaultAngleAdjustment = getDefaultAngleAdjustment();
            direction = getAngleAdjustedDirection(frames, onlyAdjustAngleVertically ? { y: defaultAngleAdjustment.y } : defaultAngleAdjustment);
        }
        if (!margin) {
            margin = 0.1;
        }
        const camera = this.createCamera({ x: 0, y: 1, z: 9 }, { x: -0.6, y: -0.8, z: 1 });
        const objectIds = frames
            .flatMap(f => {
            return [
                ...HierarchyUtils.children(f).flatMap(fc => {
                    return fc
                        .getComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)
                        ?.viewerObject.getViewerObjectId();
                }),
                f
                    .getComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)
                    ?.viewerObject.getViewerObjectId()
            ];
        })
            .filter(Boolean);
        if (shouldFocus && objectIds.length > 0) {
            if (isMobile) {
                await camera
                    .frame_objects(objectIds, false, 0.1, direction, 1 / 1)
                    .then(() => {
                    // After framing objects, the camera settings are reset and we will set the initial values
                    this.setLimits(camera, isMobile);
                })
                    .catch(e => {
                    this.coreApi.reportErrorToSentry(new Error(e), { objectIds });
                    // If framing objects fails, we will reset the camera
                    this.resetCamera();
                });
            }
            else {
                await camera
                    .frame_objects(objectIds, true, margin, direction)
                    .then(() => {
                    // After framing objects, the camera settings are reset and we will set the initial values
                    this.setLimits(camera);
                })
                    .catch(e => {
                    this.coreApi.reportErrorToSentry(new Error(e), { objectIds });
                    // If framing objects fails, we will reset the camera
                    this.resetCamera();
                });
            }
        }
        const cameraManager = this.ipexViewer.get_camera_manager();
        if (!cameraManager)
            return;
        this.ipexViewer.on_current_scene_ready().then(() => {
            cameraManager.replace_camera(camera, {
                duration_seconds: 1.2,
                interpolation: smooth_step
            });
        });
    }
    async focusSolutionAfterLoad(isMobile, shouldFocus) {
        setTimeout(() => {
            this.focusSolution(undefined, undefined, isMobile, shouldFocus);
        }, focusSolutionDelay);
    }
    getDirectionFromWalls(walls) {
        // Single wall solution should capture from angle
        const frontDirection = { x: -0.5, y: 0, z: -1 };
        const backDirection = { x: 0.5, y: 0, z: 1 };
        const leftDirection = { x: -1, y: 0, z: 0.5 };
        const rightDirection = { x: 1, y: 0, z: -0.5 };
        const frontLeftDirection = { x: -1, y: 0, z: -1 };
        const frontRightDirection = { x: 1, y: 0, z: -1 };
        const backLeftDirection = { x: -1, y: 0, z: 1 };
        const backRightDirection = { x: 1, y: 0, z: 1 };
        const defaultFrontDirection = { x: 0, y: 0, z: -1 };
        const defaultBackDirection = { x: 0, y: 0, z: 1 };
        if (walls.length === 1) {
            if (walls.includes('front'))
                return frontDirection;
            if (walls.includes('left'))
                return leftDirection;
            if (walls.includes('right'))
                return rightDirection;
            if (walls.includes('back'))
                return backDirection;
        }
        if (walls.length === 2) {
            if (walls.includes('front') && walls.includes('left'))
                return frontLeftDirection;
            if (walls.includes('front') && walls.includes('right'))
                return frontRightDirection;
            if (walls.includes('back') && walls.includes('left'))
                return backLeftDirection;
            if (walls.includes('back') && walls.includes('right'))
                return backRightDirection;
        }
        if (walls.length === 3) {
            if (walls.includes('back') && walls.includes('left') && walls.includes('right'))
                return defaultBackDirection;
        }
        return defaultFrontDirection;
    }
    getActiveCamera() {
        return this.ipexViewer.get_active_camera();
    }
    async captureScreenshot(cameraSettings, visibleObjects, direction) {
        if (!this.screenshotTool) {
            throw new Error('Failed to capture screenshot');
        }
        const temporaryCamera = this.coreApi.camera.createCamera();
        if (!temporaryCamera) {
            throw new Error('Failed to create camera when taking screenshot');
        }
        const aspectRatio = cameraSettings ? cameraSettings.size.x / cameraSettings.size.y : 4 / 3;
        temporaryCamera.projection.aspect_ratio = aspectRatio;
        if (!visibleObjects.length || visibleObjects.length === 0) {
            return '';
        }
        try {
            await temporaryCamera.frame_objects(visibleObjects, true, 0.02, direction, aspectRatio);
            const screenshot = await temporaryCamera.take_screenshot(960);
            return screenshot || '';
        }
        catch (error) {
            throw new Error('Failed to capture screenshot', error);
        }
        finally {
            temporaryCamera.dispose();
        }
    }
    // Returns a regular OrbitPlannerCamera
    createCamera(position, target) {
        if (position && target) {
            return new OrbitPlannerCamera(position, target, this.ipexViewer);
        }
        return new OrbitPlannerCamera({ x: 0, y: 1, z: 9 }, { x: 0, y: 1, z: 1 }, this.ipexViewer);
    }
    // Push a new camera to the stack if we can't find one currently in the stack
    restoreCamera() {
        if (!this.getActiveCamera()) {
            const cameraManager = this.ipexViewer.get_camera_manager();
            if (!cameraManager) {
                throw Error('No camera manager found when restoring the camera, are you sure the scene is ready?');
            }
            else {
                const camera = this.createCamera();
                cameraManager.push_camera(camera, {});
            }
        }
    }
    async captureStaticScreenshot(cameraSettings, position, target) {
        try {
            if (!this.screenshotTool) {
                throw Error('Failed to capture screenshot');
            }
            const screenshot = await this.screenshotTool.capture_screenshot_using_position_and_target(position, target, cameraSettings ?? {});
            return screenshot || '';
        }
        catch (error) {
            throw Error('Failed to capture screenshot. Error: ', error);
        }
    }
    async captureScreenshotForAssemblyInstructionOverview(cameraSettings, frames, doorObjectIds, isMultiwallSolution) {
        try {
            // Gets the position for all the frames in the viewer.
            // We do this because if there are no instructions for a specific frame we would not get any positions
            const positions = frames.map(frame => frame.position);
            let cameraDirection;
            const frameIds = frames.flatMap(frame => frame.frameObjectIds);
            // All objects that should be visible when taking the screenshot
            const objectsToHide = [...doorObjectIds];
            // We pass the initial values from the viewer to so they will stay the same after we have applied our visibility filter
            const initialVisibility = (id) => this.ipexViewer.get_visible(id);
            const visibleFilter = (id) => {
                return !objectsToHide.includes(id) && initialVisibility(id);
            };
            if (isMultiwallSolution) {
                cameraDirection = { x: 0, y: -2, z: -1 };
            }
            else {
                cameraDirection = { x: 0, y: 0, z: -1 };
            }
            const screenshot = await this.screenshotTool
                .create_screenshot_for_assembly_intructions(frameIds, cameraDirection, positions, cameraSettings || {}, defaultPadding, visibleFilter)
                .then(({ image, points }) => {
                return { image, points };
            });
            return screenshot;
        }
        catch (error) {
            throw Error(`Failed to capture screenshot: ${error}`);
        }
    }
    async captureScreenshotForAssemblySingleFrameImage(cameraSettings, objectsInFocus, holeObjectIds, objectsToHide, holePositions, padding) {
        try {
            const frameWorldDirection = this.ipexViewer.get_world_direction_from_local_direction(objectsInFocus[0], { x: 0, z: -1, y: 0 });
            const visible = [...objectsInFocus, ...holeObjectIds];
            const visibleFilter = (id) => visible.includes(id) && !objectsToHide.includes(id);
            const screenshot = await this.ipexViewer.on_current_scene_ready().then(() => this.screenshotTool
                .create_screenshot_for_assembly_intructions(objectsInFocus, frameWorldDirection, holePositions, cameraSettings || {}, padding ? { top: 0, bottom: 0, left: 0, right: 0 } : assemblySingleImagePadding, visibleFilter)
                .then(({ image, points }) => {
                return { image, points };
            }));
            return screenshot;
        }
        catch (error) {
            throw Error(`Failed to capture assembly instruction image: ${error} `);
        }
    }
}
//# sourceMappingURL=BaseCameraApi.js.map